/**
 * Where the boat will be if nothing changes.
 *
 * A dead-reckoning extrapolation from the current fix: run the reported course
 * over ground at the reported speed over ground for N seconds. That is all it
 * is, and the limits matter more than the arithmetic:
 *
 * - **It is not a route and it is not depth-checked.** Nothing here consults
 *   the chart. A prediction line lying across a rock does not mean the app
 *   knows about the rock, and — far more dangerous — a line that looks clear is
 *   not a statement that it is. Route checking (§4.4) is the thing that reads
 *   the chart, and this deliberately stays separate from it so the two cannot
 *   be confused.
 * - **It assumes the helm does nothing.** Six minutes of straight line is a
 *   long time on a boat. The far end of a five-minute vector is a hypothesis,
 *   not a prediction, which is why the horizons are drawn as separate marks
 *   rather than as one confident arrow: the skipper can see how far out each
 *   claim is.
 * - **It is only as good as the GPS.** Course over ground from a consumer
 *   receiver is noise below about a knot — the device is computing a bearing
 *   between two nearly identical positions — so below a threshold there is no
 *   course to extrapolate and this returns nothing rather than pointing
 *   confidently in a made-up direction.
 *
 * Great-circle rather than rhumb: over the few hundred metres to a few miles
 * this covers the difference is far below GPS noise, and `destinationPoint` is
 * already the tested primitive.
 */

import { type LatLon, destinationPoint } from './geo';

/**
 * Below this speed, course over ground is not meaningful.
 *
 * 0.5 m/s is a touch under one knot. The same threshold already governs whether
 * the course vector is drawn at all, and the two must agree or the boat would
 * show a heading line with no prediction on it.
 */
export const MIN_PREDICTION_SPEED_MS = 0.5;

/** Horizons the user can choose between, in seconds. */
export const PREDICTION_HORIZONS_S = [30, 60, 300, 600] as const;
export type PredictionHorizon = (typeof PREDICTION_HORIZONS_S)[number];

/** Default: half a minute, a minute, five minutes. */
export const DEFAULT_PREDICTION_HORIZONS_S: readonly number[] = [30, 60, 300];

export interface PredictionPoint {
  /** Seconds ahead of the fix. */
  seconds: number;
  position: LatLon;
  /** Distance run to reach it, metres. */
  distanceM: number;
}

export interface PredictionInput {
  position: LatLon;
  /** Course over ground, degrees true. Null means no usable course. */
  courseDeg: number | null;
  /** Speed over ground, metres per second. Null means unknown. */
  speedMs: number | null;
}

/**
 * Whether there is enough information to predict anything at all.
 *
 * Exported because the UI needs to explain the absence — a vector that silently
 * vanishes at low speed looks like a bug, and a skipper who does not know why
 * it is missing may assume the boat is stopped when the GPS is merely unsure.
 */
export function canPredict(input: PredictionInput): boolean {
  return input.courseDeg !== null
    && Number.isFinite(input.courseDeg)
    && input.speedMs !== null
    && Number.isFinite(input.speedMs)
    && input.speedMs >= MIN_PREDICTION_SPEED_MS;
}

/**
 * Predicted positions at each horizon, nearest first.
 *
 * Empty when there is no usable course or speed — never a zero-length vector
 * pointing north, which is what a naive implementation produces from a null
 * course and is indistinguishable from a real northerly heading.
 *
 * Horizons are sorted and de-duplicated so the caller cannot produce a line
 * that doubles back on itself by passing them out of order.
 */
export function predictPositions(
  input: PredictionInput,
  horizonsS: readonly number[] = DEFAULT_PREDICTION_HORIZONS_S,
): PredictionPoint[] {
  if (!canPredict(input)) return [];

  const speedMs = input.speedMs as number;
  const courseDeg = input.courseDeg as number;

  const horizons = [...new Set(horizonsS)]
    .filter((seconds) => Number.isFinite(seconds) && seconds > 0)
    .sort((a, b) => a - b);

  return horizons.map((seconds) => {
    const distanceM = speedMs * seconds;
    return {
      seconds,
      distanceM,
      position: destinationPoint(input.position, courseDeg, distanceM),
    };
  });
}

/**
 * The prediction as a polyline, starting at the boat.
 *
 * One line through every horizon rather than a separate line each, so the
 * drawn track is continuous and the horizon marks sit on it.
 */
export function predictionPath(
  input: PredictionInput,
  horizonsS: readonly number[] = DEFAULT_PREDICTION_HORIZONS_S,
): LatLon[] {
  const points = predictPositions(input, horizonsS);
  if (points.length === 0) return [];
  return [input.position, ...points.map((point) => point.position)];
}

/** "30s", "1 min", "5 min" — how a horizon is labelled on the chart. */
export function formatHorizon(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  const rounded = Number.isInteger(minutes) ? minutes : Math.round(minutes * 10) / 10;
  return `${rounded} min`;
}
